import React from "react";

import { Account, AccountAlert, User } from "../../interfaces";
import { AlertSettingsView } from "./views";
import { EditAlertSettingsProps } from "./interfaces";
import { useCurrentSsoUser } from "../../hooks/auth";
import { useAccountsApi } from "../../hooks/use-accounts";
import { useUsersApi } from "../../hooks/use-users";
import { AccountApplicableFilters, useEventApi } from "../../hooks/use-event";
import { filterEventTypes } from "../EventFilters/utils";
import { populateNewAlertSettings } from "./utils";

/**
 * A container to handle updating alert settings for an account
 *
 * It uses the account for which the current user is signed in
 */
export const AlertSettingsForm: React.FC = () => {
  const { user } = useCurrentSsoUser();

  if (!user) return null;

  return <EditAlertSettings accountId={user.accountId} />;
};

const EditAlertSettings: React.FC<EditAlertSettingsProps> = ({ accountId }) => {
  const [accountData, setAccountData] = React.useState<Account | undefined>(
    undefined
  );
  const [userData, setUserData] = React.useState<User[] | undefined>(undefined);
  const [
    applicableFilters,
    setApplicableFilters,
  ] = React.useState<AccountApplicableFilters>();
  const {
    loading,
    error,
    updating,
    updateSuccess,
    getAccount,
    updateAccountAlerts,
  } = useAccountsApi();
  const { loading: usersLoading, getEmailAlertUsers } = useUsersApi();
  const { getAccountApplicableFilters } = useEventApi();

  React.useEffect(() => {
    const fetchFilters = async () => {
      const filters = await getAccountApplicableFilters();
      setApplicableFilters(filters);
    };
    fetchFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to run this once
  }, []);

  React.useEffect(() => {
    async function fetchData() {
      const accountData = await getAccount(accountId);
      const userData = await getEmailAlertUsers(accountId);
      setAccountData(accountData);
      setUserData(userData);
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to run this once
  }, [accountId]);

  const filteredTypes = React.useMemo(
    () => filterEventTypes(true, applicableFilters),
    [applicableFilters]
  );

  /**
   * Handle form submit
   *
   * Transforms the form data into the shape required by the API and posts it
   */
  const handleSubmit = async (accountId: string, values: AccountAlert) => {
    updateAccountAlerts(accountId, values);
    setAccountData((previous) => {
      return previous ? { ...previous, alerts: values } : previous;
    });
  };

  return (
    <AlertSettingsView
      values={populateNewAlertSettings(accountData)}
      users={userData}
      filteredEvents={filteredTypes}
      accountId={accountData?.id}
      loading={loading || usersLoading}
      error={error}
      onSubmit={handleSubmit}
      isSubmitting={updating}
      success={updateSuccess}
    />
  );
};
